import React, { useEffect, useRef } from 'react';
import * as styles from './SmartCheckout.module.css';

const ACTION_NAMES = [
  'confirmed',
  'denied',
  'error',
  'close',
  'failed',
  'pending',
  'click',
  'pageview',
  'pixkey_impression',
  'read-session-failed',
];

/**
 * Converte action name para o nome do callback prop.
 * Ex: 'confirmed' → 'onConfirmed', 'read-session-failed' → 'onReadSessionFailed'
 */
const toCallbackName = (action: string): string => {
  const camel = `on_${action}`.replace(/([-_][a-z])/gi, match =>
    match.toUpperCase().replace('-', '').replace('_', ''),
  );
  return camel;
};

/**
 * Extrai os dados do evento recebido via postMessage.
 * O SDK pode enviar os dados em diferentes formatos:
 *   - event.data.data (mais comum)
 *   - event.data.payload
 *   - event.data (direto)
 *
 * Caso especial: quando data.data é uma string e status é 'error',
 * normaliza para { status: 'error' } para evitar dados inconsistentes.
 */
const getEventData = (data: any): Record<string, unknown> => {
  if (typeof data?.data === 'string' && data?.status === 'error') {
    return { status: 'error' };
  }

  return data?.data || data?.payload || data;
};

interface SmartCheckoutProps {
  url?: string;
  iframeHeight?: number;
  enabled?: boolean;
  onAllMessage?: (data: Record<string, unknown>) => void;
  onLoad?: () => void;
  onLoopEvent?: (eventData?: Record<string, unknown>) => void;
  [key: string]: unknown;
}

function SmartCheckout({
  url,
  iframeHeight,
  enabled = true,
  onAllMessage = () => {},
  onLoad = () => {},
  onLoopEvent = () => {},
  ...events
}: SmartCheckoutProps) {
  const savedOnMessage = useRef<((event: MessageEvent) => void) | undefined>(
    undefined,
  );

  /**
   * Handler principal de postMessage.
   *
   * Fluxo:
   * 1. Valida origem (url do checkout) — ignora mensagens de outras origens
   * 2. Extrai dados do evento (getEventData)
   * 3. Remove prefixo 'checkout:' do type (ex: 'checkout:confirmed' → 'confirmed')
   * 4. Determina o nome do evento: status (lowercase) ou type
   * 5. Se é um action name conhecido, envia eventLoop via onLoopEvent
   * 6. Chama o callback específico (ex: onConfirmed, onError)
   * 7. Chama onAllMessage com os dados do evento
   */
  function onMessage(event: MessageEvent) {
    if (url && !url.includes(event.origin)) return;

    const eventData = getEventData(event.data);

    const eventType = eventData?.type as string | undefined;
    if (eventType && eventType.includes('checkout:')) {
      eventData.type = eventType.replace('checkout:', '');
    }

    let eventName: string | null =
      (typeof eventData?.status === 'string'
        ? eventData.status.toLowerCase()
        : null) ||
      (eventData?.type as string) ||
      null;

    if (eventName && ACTION_NAMES.includes(eventName)) {
      onLoopEvent(eventData);
    }

    if (eventName) {
      eventName = eventName.replace(/-/g, '_');

      const processor = events[toCallbackName(eventName)] as
        | ((data: Record<string, unknown>) => void)
        | undefined;
      if (processor) processor(eventData);
    }

    onAllMessage(eventData);
  }

  // Mantém a referência atualizada do handler sem re-registrar o listener.
  // Isso garante que o handler sempre acessa o closure mais recente
  // enquanto o addEventListener usa sempre a mesma função wrapper.
  useEffect(() => {
    savedOnMessage.current = onMessage;
  });

  // Registra o listener quando enabled=true e remove quando enabled=false ou unmount.
  // Permite uso como listener puro (sem iframe) quando url está vazio.
  useEffect(() => {
    if (!enabled) return undefined;

    const eventListener = (event: MessageEvent) =>
      savedOnMessage.current?.(event);
    window.addEventListener('message', eventListener);

    return () => {
      window.removeEventListener('message', eventListener);
    };
  }, [enabled]);

  if (!url) return null;

  return (
    <iframe
      title="smartCheckout"
      id="checkout"
      className={styles.iframe}
      src={url}
      style={{ height: iframeHeight }}
      onLoad={onLoad}
      allow="payment; clipboard-read; clipboard-write; publickey-credentials-get; publickey-credentials-create"
    />
  );
}

export default SmartCheckout;
